import { useState, useEffect, ChangeEvent } from 'react'
import { Icon, Modal, UploadArea } from 'components'
import { toast } from 'react-toastify'
import { useSelector, useDispatch } from 'store/store'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller, useFormContext } from 'react-hook-form'
import { RequestBodyMaintainDraft } from 'services/draft/interfaces'
import { getItems } from 'store/SalesOrderContext/salesOrderSlice'
import { SalesOrderData } from 'store/SalesOrderContext/types/SalesOrderData'

import * as yup from 'yup'
import * as XLSX from 'xlsx'
import * as S from './styles'
import * as DraftOV from 'services/draft'
import { useParams } from 'react-router-dom'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ActionPerm } from 'components/Structures/Header/constants'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'

export interface UploadMaterialsProps {
  disabled?: boolean
}

interface FileUploadProps {
  fileInput?: File
}

const schema = yup.object().shape({
  fileInput: yup
    .mixed()
    .required('Não foi possível carregar o arquivo, tente novamente.')
    .test(
      'fileSize',
      'O tamanho máximo suportado é de 4mb. Reduza sua planilha e tente novamente.',
      (value) => {
        return value?.size <= 4000000
      }
    )
    .test(
      'fileType',
      'Só é possível enviar planilhas em “.xls” ou ".xlsx". Se necessário, baixe a planilha modelo.',
      (value) => {
        return (
          value?.type === 'application/vnd.ms-excel' ||
          value?.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        )
      }
    )
})

const UploadMaterials: React.FC<UploadMaterialsProps> = ({ disabled = false }) => {
  const dispatch = useDispatch()
  const { getValues } = useFormContext()
  const { orderNumber } = useParams()
  const [loading, setLoading] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [uploadFeedbackModal, setUploadFeedbackModal] = useState<boolean>(false)
  const [uploadedDraftID, setUploadedDraftID] = useState<string>('')
  const [uploadFeedback, setUploadFeedback] = useState<
    SalesOrderData['ds_item_inva_uplo_plan']
  >([])

  const { draftBody } = useSelector((state) => state.salesOrder)
  const { verifyPermission } = usePermissions()
  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.ORDER, action) || disabled
  }

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<FileUploadProps>({
    resolver: yupResolver(schema)
  })

  const fetchMaterials = () => {
    dispatch(getItems(''))
    setUploadFeedbackModal(false)
  }

  const postDraftSpreadsheet = async (
    bodyParams: Partial<RequestBodyMaintainDraft>,
    file: Blob
  ) => {
    const formData = new FormData()
    for (const key in bodyParams) {
      const value: string = bodyParams[key as keyof RequestBodyMaintainDraft] as string
      formData.append(`${key}`, value)
    }
    formData.append('file', file)

    try {
      const {
        pc_dsct,
        fl_prmt_desd,
        cd_cond_pgto,
        ds_cond_pgto,
        nr_pedi_clie,
        nr_dias_adic,
        dt_ano_mes_cota,
        dt_dsjd_pedi,
        ds_text_nota_fisc
      } = getValues()
      const data = await DraftOV.uploadSheetDraft(
        formData,
        {
          ...draftBody,
          pc_dsct,
          fl_prmt_desd,
          cd_cond_pgto,
          ds_cond_pgto,
          nr_pedi_clie,
          nr_dias_adic,
          dt_ano_mes_cota,
          dt_dsjd_pedi,
          ds_text_nota_fisc,
          ds_item_pedi_rasc: []
        },
        setUploadProgress
      )

      setLoading(false)
      const idDraft = data?.NumeroPedidoRascunho?.toString().trim() ?? orderNumber
      if (!data?.OperacaoRealizada && data?.return_code !== 0) return
      const updatedDraft = await DraftOV.getDraftById({ nr_pedi_rasc: idDraft })
      if (updatedDraft?.ds_item_inva_uplo_plan[0]?.Materials?.length) {
        setUploadedDraftID(idDraft)
        setUploadFeedbackModal(true)
        if (updatedDraft.ds_item_inva_uplo_plan[0]?.ErrorMenssage) {
          setUploadFeedback(updatedDraft.ds_item_inva_uplo_plan)
        }
      } else {
        fetchMaterials()
      }
    } catch (error) {
      toast.error(
        'Há um problema na digitação dos dados. Verifique a planilha e envie novamente.'
      )
    } finally {
      setLoading(false)
    }
  }

  const generateFeedbackSpreadsheet = () => {
    const workbook = XLSX.utils.book_new()
    const allData: string[][] = []

    uploadFeedback.forEach((erro, index) => {
      allData.push([erro.ErrorMenssage ?? ''])

      if (erro.Materials) {
        allData.push(['Cód. Material', 'Cód. Cor', 'Cód. Tamanho'])
        erro.Materials.forEach((material) => {
          allData.push([material.cd_matr, material.cd_cor, material.cd_tamh])
        })
      }

      if (index < uploadFeedback.length - 1) {
        allData.push(['', '', '', ''])
      }
    })

    const worksheet = XLSX.utils.aoa_to_sheet(allData)
    worksheet['!cols'] = [{ wch: 30 }, { wch: 13 }, { wch: 13 }, { wch: 13 }]

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Erros e Materiais')

    XLSX.writeFile(workbook, `relatório_upload_${uploadedDraftID}.xlsx`)
  }

  const onSubmit = handleSubmit((data) => {
    setLoading(true)
    const {
      pc_dsct,
      fl_prmt_desd,
      cd_cond_pgto,
      ds_cond_pgto,
      nr_pedi_clie,
      nr_dias_adic,
      dt_ano_mes_cota,
      dt_dsjd_pedi,
      ds_text_nota_fisc
    } = getValues()
    const body: Partial<RequestBodyMaintainDraft> = {
      ...draftBody,
      pc_dsct,
      fl_prmt_desd,
      cd_cond_pgto,
      ds_cond_pgto,
      nr_pedi_clie,
      nr_dias_adic,
      dt_ano_mes_cota,
      dt_dsjd_pedi,
      ds_text_nota_fisc
    }

    postDraftSpreadsheet(body, data.fileInput as File)
  })

  useEffect(() => {
    if (errors.fileInput?.message) {
      toast.error(errors.fileInput.message)
      setValue('fileInput', {} as File)
    }
  }, [errors])

  return (
    <>
      <S.UploadForm onSubmit={onSubmit}>
        <Controller
          name="fileInput"
          control={control}
          defaultValue={{} as File}
          render={({ field: { name, onChange } }) => (
            <UploadArea
              disabled={shouldDisable(ACTIONS_SCREENS.EDIT)}
              name={name}
              loading={loading}
              loadingProgress={uploadProgress}
              id="fileInput"
              accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              uploadAreaText={
                <>
                  Insira materiais através do campo “Adicionar Material”. Preencheu um
                  pedido via planilha? <strong>Clique aqui</strong> ou arraste o arquivo
                  para enviá-la.
                </>
              }
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (e.target.files) {
                  onChange(e.target.files[0])
                  onSubmit()
                  e.target.value = ''
                }
              }}
            />
          )}
        />
      </S.UploadForm>
      <Modal
        open={uploadFeedbackModal}
        setOpen={setUploadFeedbackModal}
        title="Inconsistência de tabela"
        description={
          <>
            Não foi possível inserir alguns dos materiais da planilha. <br />
            <br />
            Você pode fazer o download dos materiais com erro para sua consulta, e
            prosseguir com os materiais que estão corretos.
          </>
        }
        showClose={false}
        showReturn
        returnIcon={<Icon icon="download" size="15" />}
        returnText="Fazer download"
        returnAction={generateFeedbackSpreadsheet}
        buttonIcon={<Icon icon="chevron-right" size="15" />}
        buttonText="Prosseguir"
        closeAction={fetchMaterials}
        buttonAction={fetchMaterials}
      />
    </>
  )
}

export default UploadMaterials
